import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeDollarSign, Eye, ThumbsUp, Users, Clock, TrendingUp, Wallet,
  CircleCheck, CircleX, Hourglass, ArrowRight,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { fmtNum, timeAgo } from "../config";
import { Spinner, Avatar } from "../components/cards";
import { SignInNotice } from "./Account";

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 ${className}`}>{children}</div>
);

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <Card className="text-center">
    <Icon size={18} className={`mx-auto ${accent || "text-crimson"}`} />
    <p className="mt-2 font-heading text-2xl font-black text-white" data-testid={testId}>{value}</p>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
  </Card>
);

const statusTone = (s) => (s === "approved" || s === true ? "text-emerald-400" : s === "pending" ? "text-amber-400" : s === "rejected" ? "text-crimson" : "text-slate-300");

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export const Monetization = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const load = () => {
    if (!user) return;
    api.get("/monetization").then(setData).catch(() => setData({}));
  };
  useEffect(load, [user]);

  if (!user) return <SignInNotice what="manage monetization" />;
  if (data === null) return <Spinner />;

  const status = data.monetizationStatus || "none";
  const thresholds = data.thresholds || {};
  const apply = async () => {
    setBusy(true);
    setNote("");
    try {
      const r = await api.post("/monetization/apply", {});
      setNote(r.eligible ? "Application submitted." : "Application submitted — you'll be approved once you meet the thresholds.");
      load();
    } catch (err) {
      setNote(err.message || "Could not apply right now.");
    }
    setBusy(false);
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="monetization-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Monetization</h1>
      <p className="mt-2 text-sm text-slate-500">Earn from your content on Michael C Streamline — same rules as the mobile app.</p>

      <div className="mt-8 space-y-6">
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Status</p>
              <p className={`mt-2 flex items-center gap-2 font-heading text-xl font-bold ${statusTone(status)}`} data-testid="monetization-status">
                {status === "approved" && <CircleCheck size={18} />}
                {status === "pending" && <Hourglass size={18} />}
                {status === "rejected" && <CircleX size={18} />}
                {status === "none" ? "Not enabled" : status.charAt(0).toUpperCase() + status.slice(1)}
              </p>
            </div>
            <div className="text-right">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">CPM</p>
              <p className="mt-2 font-heading text-xl font-bold text-white" data-testid="monetization-cpm">{money(data.cpm)}</p>
            </div>
          </div>
          {status === "none" && (
            <div className="mt-5 border-t border-white/10 pt-5">
              <p className="text-sm leading-relaxed text-slate-400">
                Apply for the Creator Program. You need {fmtNum(thresholds.subscribers)} subscribers and {fmtNum(thresholds.watchHours)} watch hours.
              </p>
              <button onClick={apply} disabled={busy} data-testid="monetization-apply" className="mt-4 inline-flex items-center gap-2 rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
                {busy ? "Applying…" : "Apply for monetization"} <ArrowRight size={14} />
              </button>
            </div>
          )}
          {status === "pending" && <p className="mt-5 border-t border-white/10 pt-5 text-sm text-amber-400">Your application is being reviewed. This updates automatically with the mobile app.</p>}
          {status === "rejected" && <p className="mt-5 border-t border-white/10 pt-5 text-sm text-crimson">Your application wasn't approved. Keep creating and re-apply once you meet the thresholds.</p>}
          {note && <p className="mt-3 text-sm text-slate-400" data-testid="monetization-note">{note}</p>}
        </Card>

        <div>
          <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white">Eligibility</h2>
          <div className="grid grid-cols-2 gap-4">
            <Card className="text-center">
              <Users size={18} className="mx-auto text-crimson" />
              <p className="mt-2 font-heading text-2xl font-black text-white" data-testid="elig-subs">{fmtNum(data.subscribers)} <span className="text-sm text-slate-500">/ {fmtNum(thresholds.subscribers)}</span></p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Subscribers</p>
            </Card>
            <Card className="text-center">
              <Clock size={18} className="mx-auto text-crimson" />
              <p className="mt-2 font-heading text-2xl font-black text-white" data-testid="elig-hours">{fmtNum(Math.round(data.watchHours))} <span className="text-sm text-slate-500">/ {fmtNum(thresholds.watchHours)}</span></p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Watch hours</p>
            </Card>
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white">Performance</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat icon={Eye} label="Views" value={fmtNum(data.views)} testId="perf-views" />
            <Stat icon={ThumbsUp} label="Likes" value={fmtNum(data.likes)} testId="perf-likes" />
            <Stat icon={TrendingUp} label="Monetized views" value={fmtNum(data.monetizedViews)} testId="perf-monetized-views" />
            <Stat icon={BadgeDollarSign} label="Revenue" value={money(data.revenue)} accent="text-emerald-400" testId="perf-revenue" />
          </div>
        </div>

        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-bold tracking-tight text-white">Creator Earnings</h2>
              <p className="mt-1 text-sm text-slate-500">Balances, payouts and withdrawal settings.</p>
            </div>
            <Link to="/app/earnings" data-testid="view-earnings-link" className="inline-flex items-center gap-2 rounded-full border border-crimson/40 px-5 py-2.5 font-heading text-xs font-bold text-crimson transition-colors hover:bg-crimson/10">
              View earnings <ArrowRight size={14} />
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export const Earnings = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/monetization/earnings").then(setData).catch(() => setData({}));
  }, [user]);

  if (!user) return <SignInNotice what="see your earnings" />;
  if (data === null) return <Spinner />;

  const history = data.payoutHistory || [];
  const monthly = data.monthlyEarnings || [];

  return (
    <div className="mx-auto max-w-3xl" data-testid="earnings-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Creator Earnings</h1>
      <p className="mt-2 text-sm text-slate-500">What you've earned from your content.</p>

      <div className="mt-8 space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card className="text-center">
            <Wallet size={18} className="mx-auto text-emerald-400" />
            <p className="mt-2 font-heading text-2xl font-black text-emerald-400" data-testid="earnings-available">{money(data.availableBalance)}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Available</p>
          </Card>
          <Card className="text-center">
            <Hourglass size={18} className="mx-auto text-amber-400" />
            <p className="mt-2 font-heading text-2xl font-black text-amber-400" data-testid="earnings-pending">{money(data.pendingBalance)}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Pending</p>
          </Card>
          <Card className="text-center">
            <TrendingUp size={18} className="mx-auto text-crimson" />
            <p className="mt-2 font-heading text-2xl font-black text-white" data-testid="earnings-lifetime">{money(data.lifetimeEarnings)}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Lifetime</p>
          </Card>
        </div>

        <Card>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Revenue share</p>
              <p className="mt-2 font-heading text-lg font-bold text-white" data-testid="earnings-share">{data.revenueSharePercentage ?? 40}%</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Min payout</p>
              <p className="mt-2 font-heading text-lg font-bold text-white" data-testid="earnings-minpayout">{money(data.minPayout)}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Payout method</p>
              <p className="mt-2 font-heading text-lg font-bold capitalize text-white" data-testid="earnings-method">{data.payoutMethod || "stripe"}</p>
            </div>
          </div>
          <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-slate-400">
            You keep {data.revenueSharePercentage ?? 40}% of revenue. Payouts are sent automatically once your available balance reaches {money(data.minPayout)}. Payouts are managed with Stripe in the phone app; this page shows your live balance, shared with the app.
          </p>
        </Card>

        <div>
          <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white">Payout history</h2>
          {history.length === 0 ? (
            <Card><p className="text-sm text-slate-500" data-testid="earnings-no-payouts">No payouts yet.</p></Card>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-white/10">
              {history.map((p, i) => (
                <div key={p.id || i} className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3 last:border-0" data-testid={`payout-${i}`}>
                  <div>
                    <p className="font-heading text-sm font-semibold text-white">{money(p.amount)}</p>
                    <p className="text-xs text-slate-500">{timeAgo(p.createdAt || p.date)}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${p.status === "paid" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{p.status || "pending"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {monthly.length > 0 && (
          <div>
            <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white">Monthly earnings</h2>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              {monthly.map((m, i) => (
                <div key={i} className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3 last:border-0" data-testid={`monthly-${i}`}>
                  <p className="text-sm text-slate-300">{m.month || m.label}</p>
                  <p className="font-heading text-sm font-bold text-white">{money(m.amount || m.earnings)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Monetization;
